import { useAnalytics } from '@/contexts/AnalyticsContext';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { Account } from '@superfunnel/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Calendar, MoreVert } from 'iconoir-react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
// import icon from '@/assets/images/icon.png';

interface IWorkspacesList {
  accounts: QueryDocumentSnapshot<Account>[];
  onDeleteAccountClick: (account: QueryDocumentSnapshot<Account>) => void;
}

export const WorkspacesList = ({
  accounts,
  onDeleteAccountClick,
}: IWorkspacesList) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { isSupportAdmin } = useContext(AuthContext);
  return (
    <div
      role="list"
      className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
    >
      {accounts.map((account) => {
        const accountData = account.data();
        return (
          <div
            key={account.id}
            className="font-inter hover:bg-base-50 relative cursor-pointer overflow-hidden rounded-2xl bg-white p-4 shadow-[0px_1px_2px_0px_rgba(20,28,37,0.05)] ring-gray-200 hover:ring"
            onClick={() => {
              track('workspace_card_clicked', {
                account_name: accountData.name,
                account_slug: accountData.slug,
                has_logo: !!accountData.metadata?.logo,
              });
              navigate('/workspace/' + accountData.slug);
            }}
          >
            <div className="border-base-200 mb-4 flex items-center gap-x-4 border-b pb-4">
              {accountData.metadata?.logo ? (
                <img
                  src={accountData.metadata.logo.url}
                  alt={accountData.name}
                  className="border-base-200 size-12.5 flex-none rounded-xl border object-contain p-1"
                />
              ) : (
                <div className="flex size-12.5 flex-none items-center justify-center rounded-xl bg-[#E7DEFF] object-cover">
                  <span className="font-manrope text-brand-800 text-[22px] font-medium capitalize">
                    {accountData.name.slice(0, 1)}
                  </span>
                </div>
              )}
              <div className="text-base-900 text-lg/[1.4] tracking-[0.01em]">
                {accountData.name}
              </div>

              <DropdownMenu
                onOpenChange={(open) => {
                  if (open) {
                    track('workspace_menu_opened', {
                      account_name: accountData.name,
                      account_slug: accountData.slug,
                    });
                  }
                }}
              >
                <DropdownMenuTrigger
                  asChild
                  onClick={(e: any) => e.stopPropagation()}
                >
                  <button className="absolute top-0 right-0 p-4.5">
                    <MoreVert
                      strokeWidth={3}
                      className=""
                      width={20}
                      height={20}
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="bottom" align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      track('workspace_view_clicked', {
                        account_name: accountData.name,
                        account_slug: accountData.slug,
                        source: 'dropdown_menu',
                      });
                      navigate('/workspace/' + accountData.slug);
                    }}
                  >
                    View Workspace
                  </DropdownMenuItem>
                  {isSupportAdmin && (
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteAccountClick(account);
                      }}
                    >
                      Delete Workspace
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="text-base-600 flex items-center justify-between text-[14px]/[1.2] font-normal">
              <div className="flex items-center justify-between gap-2.5 -tracking-[0.01em]">
                {accountData.metadata?.members &&
                  accountData.metadata.members.length > 0 && (
                    <>
                      <div className="flex items-center">
                        {accountData.metadata.members.map((member, index) =>
                          member.photoURL ? (
                            <img
                              key={member.userId}
                              src={member.photoURL}
                              alt={member.name || member.email || ''}
                              title={member.name || member.email}
                              className={`size-6 rounded-full border border-white bg-white object-cover ${index > 0 ? '-ml-2.5' : ''}`}
                            />
                          ) : (
                            <div
                              key={member.userId}
                              className={`flex size-6 items-center justify-center rounded-full border border-white bg-[#E7DEFF] text-xs font-medium ${index > 0 ? '-ml-2.5' : ''}`}
                              title={member.name || member.email}
                            >
                              {(member.name || member.email || '?')
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          ),
                        )}
                      </div>
                      <p>
                        {accountData.metadata.membersCount ||
                          accountData.metadata.members.length}{' '}
                        {(accountData.metadata.membersCount ||
                          accountData.metadata.members.length) === 1
                          ? 'member'
                          : 'members'}
                      </p>
                    </>
                  )}
              </div>
              <div
                className={'flex items-center gap-2 py-0.5 tracking-[0.04em]'}
              >
                <Calendar width={20} height={20} />
                <time>
                  {accountData.createdAt.toDate().toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </time>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
